// vendors
import {
  ContentState,
  EditorState,
  Modifier,
  SelectionState,
  convertToRaw,
} from "draft-js";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";
import { MESSAGE_TYPES } from "../../../../utils/Enum";
import { whatsappDecodeHTMLEntities } from "../../../../../Utils";

export const IMAGE_TYPES = [
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/gif",
];
export const VIDEO_TYPES = ["video/mp4", "video/3gp"];
export const AUDIO_TYPES = [
  "audio/aac",
  "audio/mp4",
  "audio/mpeg",
  "audio/AMR",
];
export const STICKER_TYPES = ["image/webp"];

export const DOCUMENT_TYPES = [
  "text/csv",
  "text/plain",
  "application/pdf",
  "application/vnd.ms-powerpoint",
  "application/msword",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
];

export enum ImageState {
  INITIAL = "initial",
  SEARCH = "search",
  GALLERY = "gallery",
  UPLOADED = "uploaded",
}

export const getCorrectMediaFormats = (mediaType: string) => {
  if (mediaType === "video") {
    return VIDEO_TYPES;
  } else if (mediaType === "image") {
    return IMAGE_TYPES;
  } else if (mediaType === "document") {
    return DOCUMENT_TYPES;
  } else if (mediaType === "sticker") {
    return STICKER_TYPES;
  } else if (mediaType === "audio") {
    return AUDIO_TYPES;
  } else {
    return [];
  }
};
export const getUploadedMediaType = (mimeType: string) => {
  if (IMAGE_TYPES?.includes(mimeType)) {
    return "image";
  } else if (VIDEO_TYPES?.includes(mimeType)) {
    return "video";
  } else if (DOCUMENT_TYPES?.includes(mimeType)) {
    return "document";
  } else if (AUDIO_TYPES?.includes(mimeType)) {
    return "audio";
  } else {
    return "document";
  }
};
export const getCorrectMediaSize = (mediaType: string) => {
  if (mediaType === "video") {
    return 16777216;
  } else if (mediaType === "image") {
    return 5242880;
  } else if (mediaType === "document") {
    return 104857600;
  } else if (mediaType === "sticker") {
    return 1048576;
  } else if (mediaType === "audio") {
    return 5242880;
  } else {
    return 1048576;
  }
};

export const getMessageMediaType = (type: string) => {
  if (type === "image") {
    return MESSAGE_TYPES.IMAGE;
  } else if (type === "document") {
    return MESSAGE_TYPES.FILE;
  } else if (type === "video") {
    return MESSAGE_TYPES.VIDEO;
  } else if (type === "audio") {
    return MESSAGE_TYPES.AUDIO;
  } else {
    return MESSAGE_TYPES.FILE;
  }
};

export const sizeInMB = (sizeInBytes: number) => {
  return (sizeInBytes / (1024 * 1024)).toFixed(2);
};

export const getInitialEditorText = (text: string) => {
  const contentBlock = htmlToDraft(text);
  let initialEditorState = EditorState.createEmpty();
  if (contentBlock) {
    const contentState = ContentState.createFromBlockArray(
      contentBlock.contentBlocks,
    );
    const editorState = EditorState.createWithContent(contentState);
    initialEditorState = editorState;
    initialEditorState = EditorState.moveFocusToEnd(editorState); // Move the focus to the end

    // Alternatively, if you want to move the cursor to the end:
    // initialEditorState = EditorState.forceSelection(editorState, editorState.getSelection().merge({
    //   anchorOffset: editorState.getCurrentContent().getPlainText().length,
    //   focusOffset: editorState.getCurrentContent().getPlainText().length,
    // }));
  }
  return initialEditorState;
};

export const getHTMLContent = (editorState: EditorState) => {
  // return draftToHtml(convertToRaw(editorState.getCurrentContent()));

  const rawContent = convertToRaw(editorState?.getCurrentContent());
  const htmlContent = draftToHtml(rawContent);

  // // Create a temporary div element to parse the HTML content
  // const tempDiv = document?.createElement("div");
  // tempDiv.innerHTML = htmlContent;

  // // Find all anchor tags and add the desired attributes

  // const anchorTags = tempDiv.getElementsByTagName("a");
  // for (let i = 0; i < anchorTags.length; i++) {
  //   anchorTags?.[i]?.setAttribute("rel", "noopener noreferrer");
  //   anchorTags?.[i]?.setAttribute("target", "_blank");
  // }

  // // Serialize the modified content back to HTML
  // // const serializer = new XMLSerializer();
  // // const modifiedHtmlContent = serializer?.serializeToString(tempDiv);

  // // Get the inner HTML of the temporary div without the parent div
  // const modifiedHtmlContent = tempDiv.innerHTML;

  // return modifiedHtmlContent;
  return htmlContent;
};

export function moveFocusToEnd(editorState: EditorState) {
  editorState = EditorState.moveSelectionToEnd(editorState);
  return EditorState.forceSelection(editorState, editorState.getSelection());
}

export const convertHtmlToWhatsappText = (text: string) => {
  if (!text) return "";
  let strBold = text;
  let replacedBold = strBold
    ?.replace(/<strong>/g, "*")
    ?.replace(/<\/strong>/g, "*");

  let strItalic = replacedBold;
  let replacedItalic = strItalic
    ?.replace(/<em>/g, "_")
    ?.replace(/<\/em>/g, "_");

  let strStrike = replacedItalic;
  let replacedStrike = strStrike
    ?.replace(/<del>/g, "~")
    ?.replace(/<\/del>/g, "~");

  let strMono = replacedStrike;
  let replacedMono = strMono
    ?.replace(/<tt>/g, "```")
    ?.replace(/<\/tt>/g, "```")
    ?.replace(/<code>/g, "```")
    ?.replace(/<\/code>/g, "```");

  // Convert <br> tags to newline characters before stripping tags so line breaks are preserved
  replacedMono = replacedMono?.replace(/<br\s*\/?>(?=)/gi, "\n");

  // Handle lists (ordered & unordered) -> each <li> becomes a new line with '- '
  // First, handle ordered lists separately to keep numbering
  replacedMono = replacedMono?.replace(/<ol[^>]*>([\s\S]*?)<\/ol>/gi, (_full: string, inner: string) => {
    const items = inner.match(/<li[^>]*>([\s\S]*?)<\/li>/gi);
    if (!items) return "";
    let idx = 1;
    const lines = items.map((li) => {
      const content = li.replace(/<\/?li[^>]*>/gi, "") // remove <li> wrappers
        .replace(/<[^>]+>/g, "") // strip any nested tags left
        .trim();
      if (!content) return "";
      return `${idx++}. ${content}`;
    }).filter(Boolean);
    return (lines.length ? "\n" + lines.join("\n") : "");
  });
  // Rebuild unordered lists similarly (avoid leftover whitespace lines)
  replacedMono = replacedMono?.replace(/<ul[^>]*>([\s\S]*?)<\/ul>/gi, (_full: string, inner: string) => {
    const items = inner.match(/<li[^>]*>([\s\S]*?)<\/li>/gi);
    if (!items) return "";
    const lines = items.map((li) => {
      const content = li.replace(/<\/?li[^>]*>/gi, "")
        .replace(/<[^>]+>/g, "")
        .trim();
      if (!content) return "";
      return `- ${content}`;
    }).filter(Boolean);
    return (lines.length ? "\n" + lines.join("\n") : "");
  });

  // Preserve paragraph / block boundaries by inserting newlines for block-level elements before stripping tags
  replacedMono = replacedMono
    // Closing block tags to newline (avoid duplicating if newline already there)
    ?.replace(/<\/(p|div|h[1-6])>\s*/gi, "\n")
    // Opening block tags removed
    ?.replace(/<(p|div|h[1-6])[^>]*>/gi, "");

  // Convert any explicit empty paragraph markers (<p></p>) that slipped through into blank lines
  replacedMono = replacedMono?.replace(/\n?\s*<p>\s*<\/p>\s*\n?/gi, "\n\n");

  let whatsappText: string = getTextWithoutHtml(replacedMono || "")
    .replace(/&nbsp;/g, "")
    .replace(/^\n+/, ""); // trim leading newlines from list conversion
  
    // Decode HTML entities
  whatsappText = whatsappDecodeHTMLEntities(whatsappText);

  // (Preserve user-entered blank lines; no collapsing applied)
  // Trim trailing whitespace on each line
  whatsappText = whatsappText.replace(/[\t ]+\n/g, "\n");
  // Remove leading indentation before bullets / numbered items (HTML pretty-print indentation)
  whatsappText = whatsappText.replace(/^[\t ]+(- |\d+\. )/gm, '$1');
  // Generic safeguard: ensure any bullet (- ) or numbered list (\d+. ) starts on a new line
  // This avoids relying on a specific preceding character like a colon.
  // Previous implementation used a regex that could split multi-digit numbers (e.g., 10. -> 1\n0.)
  // We now scan markers and insert a newline only when:
  //  - marker is '- ' or '<number>. '
  //  - previous char is not a newline
  //  - for numbered markers, previous char is not a digit (avoids splitting '10.')
  whatsappText = (() => {
    const markerRegex = /(- |\d+\. )/g;
    let out = '';
    let lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = markerRegex.exec(whatsappText)) !== null) {
      const marker = m[0];
      const idx = m.index;
      out += whatsappText.slice(lastIndex, idx);
      const prevChar = idx > 0 ? whatsappText[idx - 1] : '';
      const isNumberMarker = /^(\d+)\. $/.test(marker);
      if (idx === 0 || prevChar === '\n' || (isNumberMarker && /\d/.test(prevChar))) {
        out += marker; // leave as-is
      } else {
        out += '\n' + marker;
      }
      lastIndex = idx + marker.length;
    }
    out += whatsappText.slice(lastIndex);
    return out;
  })();

  // Repair any previously split multi-digit ordered list numbers (e.g., lines: "1" + "0. Item" => "10. Item")
  whatsappText = (() => {
    const lines = whatsappText.split(/\n/);
    const repaired: string[] = [];
    for (let i = 0; i < lines.length; i++) {
      const cur = lines[i];
      // Collect sequences of single-digit lines (optionally separated by blank lines) followed by a line starting with digit+dot.
      if (/^\d$/.test(cur.trim())) {
        let digitBuffer = cur.trim();
        let j = i + 1;
        // Allow blank lines between digits (some editors inserted blank paragraphs)
        while (j < lines.length) {
          const probe = lines[j].trim();
            if (probe === '') { j++; continue; }
            if (/^\d$/.test(probe)) { digitBuffer += probe; j++; continue; }
            const finalMatch = /^(\d)\.\s+(.*)$/.exec(probe);
            if (finalMatch) {
              const fullNumber = digitBuffer + finalMatch[1];
              repaired.push(fullNumber + '. ' + finalMatch[2]);
              i = j; // advance outer loop to the matched line
              break;
            }
            // Not part of a split number sequence
            repaired.push(cur); // push original single digit as its own line
            break;
        }
        if (j >= lines.length) {
          // Reached end; if we didn't push inside loop, push digit line
          if (repaired[repaired.length - 1] !== cur) repaired.push(cur);
        }
        continue;
      }
      repaired.push(cur);
    }
    return repaired.join('\n');
  })();
  return whatsappText;
};

export const getTextWithoutHtml = (string: string) => {
  let strippedHtml = string?.replace(/<[^>]+>/g, "");
  return strippedHtml;
};

export const insertEditorText = (text: any, editorValue: any) => {
  const currentContent = editorValue.getCurrentContent();
  const currentSelection = editorValue.getSelection();
  const newContent = Modifier.replaceText(
    currentContent,
    currentSelection,
    text,
  );
  const newEditorState = EditorState.push(
    editorValue,
    newContent,
    "insert-characters",
  );
  return EditorState.forceSelection(
    newEditorState,
    newContent.getSelectionAfter(),
  );
};

export const getMentionCustomNameKeys = (arr: any) => {
  let a: any = [];
  arr?.forEach((item: any) => {
    let temp = {
      ...item,
      name: item?.message,
    };

    a?.push(temp);
  });
  return a;
};

export const getEditorSelectedText = (editorState: EditorState): string => {
  const selectionState: SelectionState = editorState?.getSelection();
  const contentState: ContentState = editorState?.getCurrentContent();
  const currentContent = contentState?.getPlainText();
  const start = selectionState?.getStartOffset();
  const end = selectionState?.getEndOffset();
  const selectedText = currentContent?.slice(start, end);

  return selectedText;
};

export const editorAddLink = (
  editorState: EditorState,
  selectedText: string,
): EditorState => {
  if (!selectedText) return editorState;

  const contentState: ContentState = editorState.getCurrentContent();
  const contentStateWithEntity = contentState.createEntity(
    "LINK",
    "MUTABLE",
    { url: selectedText }, // Replace with the desired URL
  );

  const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
  const selectionState: SelectionState = editorState.getSelection();
  const updatedContentState = Modifier.replaceText(
    contentStateWithEntity,
    selectionState,
    selectedText,
    undefined,
    entityKey,
  );

  const newEditorState = EditorState.push(
    editorState,
    updatedContentState,
    "insert-characters",
  );

  return EditorState.forceSelection(
    newEditorState,
    updatedContentState.getSelectionAfter(),
  );
};

export const filterEditorCannedResponsesSearch = (
  data: any[],
  searchText: string,
) => {
  const filteredData = data.filter((item: any) => {
    const { name, short_code } = item;
    const lowercaseSearchText = searchText?.toLowerCase();
    const lowercaseName = name?.toLowerCase();
    const lowercaseShortCode = short_code?.toLowerCase();

    return (
      lowercaseName?.includes(lowercaseSearchText) ||
      lowercaseShortCode?.includes(lowercaseSearchText)
    );
  });
  return filteredData || [];
};

export const getChannelWiseText = (text: string, type?: string) => {
  let finalText = text;
  if (!finalText) return "";

  // Only convert plain text URLs to links if the text doesn't already contain HTML links
  if (!finalText.includes('<a href=')) {
    finalText = htmlStringToLink(finalText);
  }

  if (type === "whatsapp") {
    finalText = convertHtmlToWhatsappText(finalText);
    return finalText;
  }
  if (type === "messenger") {
    finalText = convertHtmlToWhatsappText(finalText);
    return finalText;
  }

  finalText = finalText?.length > 0 ? replaceEmptyPTagsWithBr(finalText) : "";

  return finalText;
};

export const getHtmlTextChannelWise = (text: string, type?: string) => {
  let finalText = text;
  if (!finalText) return "";

  if (type === "whatsapp") {
    finalText = convertWhatsappToHtmlText(text);
    return finalText;
  }

  if (type === "messenger") {
    finalText = convertWhatsappToHtmlText(text);
    return finalText;
  }

  return finalText;
};

export const convertWhatsappToHtmlText = (text: string) => {
  if (!text) return "";
  let raw = text.toString();

  // Recombine any split ordered list numbers in the raw text before processing
  raw = (() => {
    const lines = raw.split(/\n/);
    const repaired: string[] = [];
    for (let i = 0; i < lines.length; i++) {
      const cur = lines[i];
      if (/^\d$/.test(cur.trim()) && i + 1 < lines.length) {
        const next = lines[i + 1];
        const m = /^(\d)\.\s+(.*)$/.exec(next.trim());
        if (m) {
          repaired.push(cur.trim() + m[1] + '. ' + m[2]);
          i++; continue;
        }
      }
      repaired.push(cur);
    }
    return repaired.join('\n');
  })();

  // Rebuild lists from hyphen bullets (- item) and numbered lines (1. item)
  const lines = raw.split(/\n/);
  const rebuilt: string[] = [];
  let inUl = false;
  let inOl = false;
  // Blank lines will be represented as empty paragraphs <p></p> so that Draft.js preserves them as distinct blocks.

  const closeLists = () => {
    if (inUl) { rebuilt.push('</ul>'); inUl = false; }
    if (inOl) { rebuilt.push('</ol>'); inOl = false; }
  };

  lines.forEach((line) => {
    const trimmed = line.trim();
    if (trimmed === "") {
      // Close any open list and add an empty paragraph to preserve a blank line.
      closeLists();
      rebuilt.push('<p></p>');
      return;
    }
    const bulletMatch = /^-\s+(.+)$/.exec(trimmed);
    const orderedMatch = /^(\d+)\.\s+(.+)$/.exec(trimmed);
    if (bulletMatch) {
      if (inOl) { rebuilt.push('</ol>'); inOl = false; }
      if (!inUl) { rebuilt.push('<ul>'); inUl = true; }
      rebuilt.push(`<li>${bulletMatch[1]}</li>`);
      return;
    }
    if (orderedMatch) {
      if (inUl) { rebuilt.push('</ul>'); inUl = false; }
      if (!inOl) { rebuilt.push('<ol>'); inOl = true; }
      rebuilt.push(`<li>${orderedMatch[2]}</li>`);
      return;
    }
    // Normal text line
    closeLists();
  // Wrap as paragraph to keep separation from following lists in htmlToDraft parsing
  rebuilt.push(`<p>${trimmed}</p>`);
  });
  closeLists();

  let htmlText = rebuilt.join("");

  const htmlFormat = [
    { symbol: "*", tag: "strong" },
    { symbol: "_", tag: "em" },
    { symbol: "~", tag: "del" },
    { symbol: "`", tag: "code" },
  ];
  htmlFormat.forEach(({ symbol, tag }) => {
    const regex = new RegExp(`\\${symbol}([^\\n${symbol}][^${symbol}\\n]*)\\${symbol}`, 'g');
    htmlText = htmlText.replace(regex, (_m, inner) => `<${tag}>${inner}</${tag}>`);
  });
  return htmlText;
};

export const checkEmptyString = (htmlText: string): string => {
  if (!htmlText) return "";

  const comparisonString1 = "<p></p>\n";
  const comparisonString2 = "<p></p>";

  if (htmlText === comparisonString1 || htmlText === comparisonString2) {
    return "";
  }

  return htmlText;
};

export const replaceEmptyPTagsWithBr = (htmlText: string) => {
  htmlText = checkEmptyString(htmlText);

  if (!htmlText) return "";

  let withoutEmptyPTags = htmlText?.replace(/<p><\/p>/g, "<br/>");
  return withoutEmptyPTags;
};

const htmlStringToLink = (htmlString: string) => {
  // Skip if the string already contains HTML links
  if (htmlString.includes('<a href=')) {
    return htmlString;
  }

  // Regular expression to match URLs that aren't already part of an HTML link
  const regex = /(?<!<a[^>]*>)(https?:\/\/[^\s<>]+)(?![^<]*<\/a>)/g;

  return htmlString.replace(regex, (url) => {
    return `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`;
  });
};

export function removeCharactersFromString(str: string, length: number = 1) {
  return str?.slice(length) ?? str;
}

export const languageOptions = [
  { label: "Afrikaans", value: "af" },
  { label: "Albanian", value: "sq" },
  { label: "Arabic", value: "ar" },
  { label: "Armenian", value: "hy" },
  { label: "Azerbaijani", value: "az" },
  { label: "Bengali", value: "bn" },
  { label: "Bosnian", value: "bs" },
  { label: "Bulgarian", value: "bg" },
  { label: "Catalan", value: "ca" },
  { label: "Chinese (Simplified)", value: "zh" },
  { label: "Chinese (Traditional)", value: "zh-TW" },
  { label: "Croatian", value: "hr" },
  { label: "Czech", value: "cs" },
  { label: "Danish", value: "da" },
  { label: "Dutch", value: "nl" },
  { label: "English", value: "en" },
  { label: "Estonian", value: "et" },
  { label: "Filipino", value: "tl" },
  { label: "Finnish", value: "fi" },
  { label: "French", value: "fr" },
  { label: "Georgian", value: "ka" },
  { label: "German", value: "de" },
  { label: "Greek", value: "el" },
  { label: "Gujarati", value: "gu" },
  { label: "Hebrew", value: "he" },
  { label: "Hindi", value: "hi" },
  { label: "Hungarian", value: "hu" },
  { label: "Icelandic", value: "is" },
  { label: "Indonesian", value: "id" },
  { label: "Italian", value: "it" },
  { label: "Japanese", value: "ja" },
  { label: "Kannada", value: "kn" },
  { label: "Kazakh", value: "kk" },
  { label: "Khmer", value: "km" },
  { label: "Korean", value: "ko" },
  { label: "Latvian", value: "lv" },
  { label: "Lithuanian", value: "lt" },
  { label: "Macedonian", value: "mk" },
  { label: "Malay", value: "ms" },
  { label: "Malayalam", value: "ml" },
  { label: "Marathi", value: "mr" }, // ✅ Your requested language
  { label: "Mongolian", value: "mn" },
  { label: "Nepali", value: "ne" },
  { label: "Norwegian", value: "no" },
  { label: "Persian", value: "fa" },
  { label: "Polish", value: "pl" },
  { label: "Portuguese", value: "pt" },
  { label: "Punjabi", value: "pa" },
  { label: "Romanian", value: "ro" },
  { label: "Russian", value: "ru" },
  { label: "Serbian", value: "sr" },
  { label: "Sinhala", value: "si" },
  { label: "Slovak", value: "sk" },
  { label: "Slovenian", value: "sl" },
  { label: "Spanish", value: "es" },
  { label: "Swahili", value: "sw" },
  { label: "Swedish", value: "sv" },
  { label: "Tamil", value: "ta" },
  { label: "Telugu", value: "te" },
  { label: "Thai", value: "th" },
  { label: "Turkish", value: "tr" },
  { label: "Ukrainian", value: "uk" },
  { label: "Urdu", value: "ur" },
  { label: "Uzbek", value: "uz" },
  { label: "Vietnamese", value: "vi" },
  { label: "Welsh", value: "cy" },
];

export const aiOptions = [
  {
    label: "Condense",
    value: "condense",
    icon: "compress",
    description: "Make text shorter and more concise"
  },
  {
    label: "Fix Grammar",
    value: "fix-grammar",
    icon: "spellcheck",
    description: "Correct grammar and spelling errors"
  },
  {
    label: "Positive Tone",
    value: "positive-tone",
    icon: "SentimentSatisfied",
    description: "Make text more positive and upbeat"
  },
  {
    label: "Translate",
    value: "translate",
    icon: "translate",
    description: "Translate to different languages",
    hasSubmenu: true
  }
];
export const replaceEditorContent = (text: any, editorValue: any) => {
  const currentContent = editorValue.getCurrentContent();
  const firstBlock = currentContent.getFirstBlock();
  const lastBlock = currentContent.getLastBlock();
  
  // Create a selection that covers the entire content
  const entireSelection = new SelectionState({
    anchorKey: firstBlock.getKey(),
    anchorOffset: 0,
    focusKey: lastBlock.getKey(),
    focusOffset: lastBlock.getLength(),
  });
  
  // Replace the entire content with new text
  const newContent = Modifier.replaceText(
    currentContent,
    entireSelection,
    text,
  );
  
  const newEditorState = EditorState.push(
    editorValue,
    newContent,
    "insert-characters",
  );
  
  return EditorState.forceSelection(
    newEditorState,
    newContent.getSelectionAfter(),
  );
};


